/**
 * Вкладка «Новый год» в кабинете ДК (29.10.2026, владелец) — модель без React и без api/client:
 * типы ответа GET /api/ny/cards (apps/api/src/routes/ny/cards.ts), поиск и фильтр на клиенте, раскладка по группам,
 * проверка формы «+ Заявка» (зеркало routes/ny/leads.ts) и подписи. Тесты — nyModel.test.ts.
 * 04.11 (v2): статус, предоплата, «Скрытые», строка квеста и фильтры по статусам — nyStatus.ts (тесты nyStatus.test.ts).
 */

/** Ручной статус клиента (ставит ДК) и статус показа: «Бронь» и «Предоплата» сервер ставит сам по броням
 *  (apps/api/src/routes/ny/statusRule.ts: Предоплата > Бронь > ручной). */
export type NyManual = "new" | "work" | "refused";
export type NyStatus = NyManual | "booked" | "prepaid";

export interface NyBooking {
  id: number; date: string; time: string; custom_time: string | null;
  /** ночной слот (00:00–05:59): день сетки — D-1, бронь открываем в grid_date */
  night: boolean; grid_date: string;
  quest_id: string; quest: string; branch_id: string; players: number; status: string; type: string; comment: string | null;
  /** предоплата брони — правится в модалке брони */
  prepaid: boolean; prepaid_amount: number;
}
export interface NyLead {
  id: number; when: string | null; people: number | null; wishes: string | null; source: string | null;
  status: string; created_at: string; by: string | null; manual: boolean;
  /** «Удалить» во вкладке: заявка видна только в фильтре «Скрытые» (с «Вернуть»), во «Лидах» — как была */
  hidden: boolean;
  /** ждёт ответа — считает сервер (routes/ny/statusRule.ts leadWaits): новая, не скрыта и пришла позже ручного «В работе» /
   *  «Отказ» и самой свежей брони — группа «Ждут ответа» и тег «заявка» */
  open: boolean;
}
export interface NyCard {
  /** id карточки (client_refs) — для заметок и скринов; null — без телефона или на номере-заглушке */
  id: number | null; key: string; name: string | null; phone: string | null;
  bookings: NyBooking[]; leads: NyLead[]; notes: number; shots: number; last_activity: string | null;
  /** статус показа и ручной статус сезона (кто и когда ставил ручной) */
  status: NyStatus; manual_status: NyManual; status_by: string | null; status_at: string | null;
  /** ручной «В работе» / «Отказ», который «вернула» новая заявка (до ответа на неё на экране «Новая»), иначе null */
  reopened_from: NyManual | null;
  /** сумма предоплат по броням (₽) */
  prepaid_total: number;
  /** последний комментарий: автор, начало текста, когда (UTC) */
  last_note: { author: string | null; text: string; at: string } | null;
}
export interface NyCardsResp { ok?: boolean; season: { from: string; to: string }; cards: NyCard[] }

const digits = (s: string | null | undefined): string => (s ?? "").replace(/\D/g, "");

/** Поиск по имени (без регистра, ё = е) и по телефону: цифры подряд в любом месте номера; начало номера с кодом страны —
 *  «+7 908…», «7908…» и «8 908…» — один номер и полностью, и по первым цифрам (у карточки номер вида 8XXXXXXXXXX). */
export function matchQuery(card: NyCard, query: string): boolean {
  const q = query.trim().toLowerCase().replace(/ё/g, "е");
  if (!q) return true;
  const name = (card.name ?? "").toLowerCase().replace(/ё/g, "е");
  if (name.includes(q)) return true;
  const d = digits(q);
  if (d.length < 3 || d.length < q.replace(/[\s()+-]/g, "").length) return false;   // в запросе есть буквы — это не номер
  const phone = digits(card.phone);
  if (phone.includes(d)) return true;
  if (d[0] !== "7" && d[0] !== "8") return false;
  /* 7/8 в начале запроса — код страны: остаток — начало номера без него */
  const local = phone.length === 11 && (phone[0] === "7" || phone[0] === "8") ? phone.slice(1) : phone;
  return local.startsWith(d.slice(1));
}

/** Группа карточки — тот же порядок, что у сервера (cards.ts group): 0 — есть заявка, что ждёт ответа (open — правило сервера:
 *  заявка после «Отказа», «В работе» или брони снова сверху, а до них — нет), 1 — впереди игра, 2 — остальные. */
export function groupOf(card: NyCard, today: string): 0 | 1 | 2 {
  if (card.leads.some((l) => l.open && !l.hidden)) return 0;
  return card.bookings.some((b) => b.status === "active" && b.date >= today) ? 1 : 2;
}
export const NY_GROUPS = ["Ждут ответа", "Впереди игра", "Остальные"] as const;

/** Раскладка по группам с заголовками; внутри группы — порядок сервера (свежая заявка / ближайшая игра / свежая активность). */
export function groupCards(cards: NyCard[], today: string): { group: 0 | 1 | 2; title: string; cards: NyCard[] }[] {
  const out: { group: 0 | 1 | 2; title: string; cards: NyCard[] }[] = [];
  for (const g of [0, 1, 2] as const) {
    const list = cards.filter((c) => groupOf(c, today) === g);
    if (list.length) out.push({ group: g, title: NY_GROUPS[g], cards: list });
  }
  return out;
}

/** Ближайшая предстоящая игра карточки (активная, не раньше сегодня) — для свёрнутой строки. */
export function nextBooking(card: NyCard, today: string): NyBooking | null {
  return card.bookings.filter((b) => b.status === "active" && b.date >= today)
    .sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`))[0] ?? null;
}

/** «Инфо о клиенте»: по брони, если есть (тот же клиент), иначе по телефону заявки; нечего показать — null. */
export function infoTarget(card: NyCard): { bookingId: number } | { phone: string } | null {
  const b = card.bookings[0];
  if (b) return { bookingId: b.id };
  return card.phone ? { phone: card.phone } : null;
}

/** День сетки для брони: сервер уже отдаёт D-1 у ночного слота; на всякий случай — дата игры. */
export const gridDateOf = (b: NyBooking): string => b.grid_date || b.date;

/** Возврат во вкладку после модалки брони, открытой из «Нового года». Объект, а не состояние React: модалка зовёт onClose
 *  через 220 мс из замыкания рендера до нажатия (BookingModal handleClose) — состояние там ещё старое. «Перенести» возврат
 *  снимает: перенос идёт в сетке (плашка и слоты — только там), во вкладку не уводим. */
export function nyReturn() {
  let id: number | null = null;
  return {
    open(bookingId: number): void { id = bookingId; },
    drop(): void { id = null; },
    /** «Перенести» в модалке: сперва снять возврат, потом включить режим переноса */
    moving<B>(start: (b: B) => void): (b: B) => void { return (b) => { id = null; start(b); }; },
    /** модалка брони закрылась: true — вернуться в «Новый год» (только та бронь, что открыли оттуда); возврат одноразовый */
    close(bookingId: number | null | undefined): boolean { const back = id != null && bookingId === id; id = null; return back; },
  };
}

/** «Обработан» у заявки в карточке: заявка остаётся (приглушена, «обработана») — не «убран из списка», как во вкладке «Лиды». */
export const NY_LEAD_DONE = { toast: "Заявка обработана — осталась в карточке", ask: "Точно обработана?" } as const;

/* ── форма «+ Заявка» — правила routes/ny/leads.ts, чтобы ошибка была видна до отправки ── */
export interface NyLeadForm { name: string; phone: string; when: string; people: string; wishes: string }
export const EMPTY_LEAD: NyLeadForm = { name: "", phone: "", when: "", people: "", wishes: "" };

/** Телефон как его приведёт сервер (normalizePhone): без пробелов, скобок, «-» и «+»; 7/8 + 10 цифр → 8 + 10. */
export function normPhone(raw: string): string {
  let p = raw.replace(/[\s\-()+]/g, "");
  if (p.length === 11 && (p[0] === "7" || p[0] === "8")) p = p.slice(1);
  return /^\d{10}$/.test(p) ? `8${p}` : p;
}

export function leadError(f: NyLeadForm): string | null {
  const name = f.name.trim();
  if (!name) return "Имя обязательно";
  if (name.length > 100) return "Имя: не длиннее 100 символов";
  if (!/^\d{10,15}$/.test(normPhone(f.phone.trim()))) return "Телефон: 10–15 цифр";
  if (f.when.trim().length > 100) return "«Когда»: не длиннее 100 символов";
  const p = f.people.trim();
  if (p && !(/^\d+$/.test(p) && Number(p) >= 1 && Number(p) <= 500)) return "Сколько человек: от 1 до 500";
  if (f.wishes.trim().length > 500) return "Пожелания: не длиннее 500 символов";
  return null;
}

/** Тело POST /api/ny/leads; «!!!» (is_ny) сервер ставит сам. */
export function leadBody(f: NyLeadForm): { client_name: string; phone: string; ny_when: string | null; ny_people: number | null; comment: string } {
  const p = f.people.trim();
  return { client_name: f.name.trim(), phone: f.phone.trim(), ny_when: f.when.trim() || null, ny_people: p ? Number(p) : null, comment: f.wishes.trim() };
}

/** Сегодня по Оренбургу (UTC+5), «YYYY-MM-DD» — как todayOren() сервера: «впереди игра» считаем от того же дня. */
export const todayOrenIso = (now = Date.now()): string => new Date(now + 5 * 3600000).toISOString().slice(0, 10);

/* ── подписи ── */
const MON = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const WD = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];

/** «2026-12-31» → «31 дек» */
export function fmtDM(date: string): string {
  const [, m, d] = date.split("-");
  return m && d ? `${Number(d)} ${MON[Number(m) - 1] ?? m}` : date;
}
export const weekday = (date: string): string => WD[new Date(`${date}T00:00:00Z`).getUTCDay()] ?? "";

/** UTC из базы («YYYY-MM-DD HH:MM:SS» или ISO) → «31 дек 14:05» по Оренбургу (UTC+5). */
export function fmtStamp(utc: string | null | undefined): string {
  if (!utc) return "";
  const t = Date.parse(utc.replace(" ", "T") + (/[zZ]|[+-]\d\d:?\d\d$/.test(utc) ? "" : "Z"));
  if (Number.isNaN(t)) return utc.slice(0, 16);
  const s = new Date(t + 5 * 3600000).toISOString();
  return `${fmtDM(s.slice(0, 10))} ${s.slice(11, 16)}`;
}

/** Ярлык источника: «Сайт НГ | page=… | utm_…» → «Сайт НГ» (дальше пишет посетитель); ручная заявка — «Вручную». */
export function sourceLabel(l: NyLead): string {
  const s = (l.source ?? "").split("|")[0]!.trim();
  return s || (l.manual ? "Вручную" : "—");
}

export function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}
